import { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { onPopupDismissed, onPopupRemindLater, onPopupRedirected } from "../utils/proPlanAnnouncement";

export default function ProPlanPopup({ onClose }) {
    const navigate = useNavigate();
    const titleId = useId();
    const dialogRef = useRef(null);

    useEffect(() => {
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        const originalOverflow = document.body.style.overflow;
        const originalPadding = document.body.style.paddingRight;
        document.body.style.overflow = "hidden";
        document.body.style.paddingRight = `${scrollbarWidth}px`;
        return () => {
            document.body.style.overflow = originalOverflow;
            document.body.style.paddingRight = originalPadding;
        };
    }, []);

    function handleClose() { onPopupDismissed(); onClose(); }
    function handleRemindLater() { onPopupRemindLater(); onClose(); }
    function handleRedirect() { onPopupRedirected(); onClose(); navigate("/professional-plan"); }

    return createPortal(
        <div
            className="fixed inset-0 z-[200] flex items-center justify-center bg-[rgba(26,24,22,0.55)] px-6 py-10"
        >
            <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                tabIndex={-1}
                className="w-full max-w-md rounded-10 border border-pl-border bg-pl-bg-elevated p-6 shadow-pl-lg outline-none md:p-8"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header row */}
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <p className="font-mono text-10 uppercase tracking-[0.2em] text-pl-accent mb-2">
                            Now available
                        </p>
                        <h2
                            id={titleId}
                            className="font-display text-24 font-semibold text-pl-text-primary"
                        >
                            Professional Plan
                        </h2>
                    </div>
                    <button
                        type="button"
                        onClick={handleClose}
                        aria-label="Close"
                        className="rounded-pl border border-transparent p-2 text-pl-text-secondary transition-colors hover:border-pl-border hover:bg-pl-bg-subtle hover:text-pl-text-primary"
                    >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                    </button>
                </div>

                {/* Description */}
                <p className="mt-3 text-15 leading-7 text-pl-text-secondary">
                    AI-powered legal research, translation, and more. Built for Sri Lankan legal professionals.
                </p>
                <p className="mt-2 text-15 leading-7 text-pl-text-secondary">
                    {`Now live on ${(import.meta.env.VITE_PRESIGNED_BASE_URL || "https://paralegal.lk").replace(/^https?:\/\//, "").replace(/^www\./, "").replace(/\/$/, "")}.`}
                </p>

                {/* Actions */}
                <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                    <button
                        type="button"
                        onClick={handleRemindLater}
                        className="rounded-pl border border-pl-border bg-pl-bg-elevated px-4 py-3 text-14 font-semibold text-pl-text-primary transition-colors hover:bg-pl-bg-subtle"
                    >
                        Remind me later
                    </button>
                    <button
                        type="button"
                        onClick={handleRedirect}
                        className="group inline-flex items-center justify-center gap-2 rounded-pl bg-pl-accent px-4 py-3 text-14 font-semibold text-white transition-colors hover:bg-pl-accent-hover"
                    >
                        View Professional Plan
                        <span className="transition-transform duration-200 group-hover:translate-x-1">→</span>
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}